/**
 * Client health score, its history and the at-risk ranking — ADR-001 §8.1, §8.11, §11.8 (guide §6.1).
 *
 * The score is an event-tier snapshot (`client_health_daily`): reasons are STORED, never recomputed for
 * display, so an old score still shows the reasons that produced it, and `config_version` says which
 * thresholds were in force. Every number carries its `explain` (§11.1).
 *
 * Tone (ADR §8.5; operator decision): the SCORE is visible to everyone who can see the client, the tone
 * REASON is not. For a caller without `toneReason.read`, the tone reasons (`TONE_SIGNALS`) are left out of
 * `explain` and the tone inputs are absent — the score still includes their points. Other email-derived
 * reasons (quiet, waiting for our reply) carry `source: 'mail'` for the "From emails" chip and stay visible.
 * This is the one rule (common.ts TONE_REASON_SIGNALS) and the schema enforces it: the member variants
 * (`ClientHealthDetailMember`, `ClientHealthMember`) refuse a tone reason or a tone input, so a response that
 * forgets to strip them fails validation. `toneReason.read` and `revenue.read` belong to the same roles (founder,
 * AD — a domain test keeps it so), which is why these routes use the revenue `byAudience` split.
 */
import { z } from 'zod';
import { IsoDate, PageQuery, TONE_REASON_SIGNALS, Timestamp, Uuid, derived, derivedMember, page } from './common.js';
import { ClientRef, HealthBand } from './clients.js';
import { UserRef } from './users.js';
// ---------------------------------------------------------------- GET /clients/{id}/health
/** Band wording (prototype `health()`): Healthy ≥ 70, Watch ≥ 45, At risk below. */
export const HEALTH_BAND_LABEL = { healthy: 'Healthy', watch: 'Watch', at_risk: 'At risk' };
/**
 * `Reason.signal` keys the health score emits (prototype `health()` at L2263, ADR §8.1). `email_followups` is
 * informational (no points). The `detail` wording is the prototype's, e.g. "Utilisation is 34% of what they pay for".
 */
export const HEALTH_SIGNALS = [
    'util_low', 'util_watch', 'trend_fall', 'trend_dip', 'overdue', 'escalations', 'request_breach',
    'quiet_hard', 'quiet_soft', 'tone', 'needs_reply', 'no_reply', 'positive_tone', 'email_followups', 'no_data',
    // ADR-003 §6.1 item 2: 0 points; the SPOC has no working Gmail, so no email signal was scored.
    'email_signals_unavailable',
];
/** Left out of `explain` for callers without `toneReason.read` (the tone reason is manager-only). */
export const TONE_SIGNALS = TONE_REASON_SIGNALS;
/** What the score was computed from (stored with the snapshot as `inputs`). */
export const HealthInputs = z
    .object({
    /** Revenue-weighted utilisation Σ(util × mrr) / Σ mrr, ignoring products with no value; `null` = no usage data. */
    weighted_util: z.number().min(0).max(400).nullable(),
    /** Revenue-weighted trend vs own baseline, %; 0 when none. */
    weighted_trend: z.number(),
    overdue_tasks: z.number().int().nonnegative(),
    open_escalations: z.number().int().nonnegative(),
    request_breaches: z.number().int().nonnegative(),
    /** Days since the last client contact; `null` = none recorded. */
    quiet_days: z.number().int().nonnegative().nullable(),
    /** Inbound emails waiting on us for the minimum days or more. */
    needs_reply: z.number().int().nonnegative(),
    /** Our outbound emails unanswered for the no-reply line or more. */
    unanswered_outbound: z.number().int().nonnegative(),
    /** Products the client buys with no usage data reported. */
    products_no_data: z.number().int().nonnegative(),
    /** Follow-up tasks from emails still open (informational, no points). */
    email_followups_open: z.number().int().nonnegative(),
    /** Tone points taken (two worst inbound, capped). Absent without `toneReason.read`. */
    tone_penalty: z.number().int().nonnegative().optional(),
    /** A positive-tone inbound email in the window. Absent without `toneReason.read`. */
    positive_tone: z.boolean().optional(),
})
    .meta({ id: 'HealthInputs' });
/**
 * Which Step 8 signal sources feed this client's score (ADR-003 §6.1 item 4), so the screen can mark the score as
 * partial. `email` is true when the client's current SPOC has Gmail connected and working, `calendar` the same for
 * Google Calendar. A source that is not connected, needs a reconnect, is blocked by the Workspace admin or was
 * disconnected is `false`. When `email` is false the client has no
 * email signals, and a score without the email penalties must not be read as reassurance.
 *
 * Read live from the SPOC's connection on every request, not stored with the snapshot: with `?as_of` it still says
 * what is connected now. The same for every audience (it carries no tone and no revenue).
 */
export const SignalCoverage = z
    .object({ email: z.boolean(), calendar: z.boolean() })
    .strict()
    .meta({ id: 'SignalCoverage' });
/**
 * `Derived<number>` + band + inputs + config_version + signal_coverage. `explain` is the reason list the screen prints, worst
 * first; each reason's `detail` is the plain-English line and its `band` colours it.
 */
export const ClientHealthDetail = derived(z.number().int().min(0).max(100))
    .extend({
    client: ClientRef,
    band: HealthBand,
    inputs: HealthInputs,
    /** The thresholds in force when this snapshot was computed (see GET /settings/thresholds). */
    config_version: z.number().int().positive(),
    computed_at: Timestamp,
    signal_coverage: SignalCoverage,
})
    .meta({ id: 'ClientHealthDetail' });
/** Inputs as a caller without `toneReason.read` receives them — strict: the tone fields may not appear. */
export const HealthInputsMember = HealthInputs.omit({ tone_penalty: true, positive_tone: true }).strict().meta({ id: 'HealthInputsMember' });
/** ClientHealthDetail without tone: no tone reason in `explain`, no tone input. */
export const ClientHealthDetailMember = derivedMember(z.number().int().min(0).max(100))
    .extend({
    client: ClientRef,
    band: HealthBand,
    inputs: HealthInputsMember,
    config_version: z.number().int().positive(),
    computed_at: Timestamp,
    signal_coverage: SignalCoverage,
})
    .strict()
    .meta({ id: 'ClientHealthDetailMember' });
/**
 * `?as_of` — a past date returns that day's stored snapshot (404 when none was taken that day); omitted =
 * the latest snapshot. A client with no snapshot yet is scored on read and returned unsaved (the worker
 * stores it), so the screen is never empty.
 */
export const HealthQuery = z.object({ as_of: IsoDate.optional() });
// ---------------------------------------------------------------- GET /clients/{id}/health/history
export const HEALTH_HISTORY_MAX_DAYS = 366;
export const HEALTH_HISTORY_DEFAULT_DAYS = 90;
const todayIst = () => new Date(Date.now() + 330 * 60_000).toISOString().slice(0, 10);
/**
 * `?from&to` — `to` defaults to today (IST); `from` defaults to `to` − 90 days. Both rules are checked against
 * the defaulted ends, so `?from=` alone is measured to today: `from` on or before the end, and at most 366 days.
 */
export const HealthHistoryQuery = z
    .object({ from: IsoDate.optional(), to: IsoDate.optional() })
    .refine((q) => !q.from || q.from <= (q.to ?? todayIst()), { message: 'Pick a start date on or before the end date', path: ['from'] })
    .refine((q) => !q.from || (Date.parse(q.to ?? todayIst()) - Date.parse(q.from)) / 86_400_000 < HEALTH_HISTORY_MAX_DAYS, { message: 'Pick a range of a year or less', path: ['from'] });
export const HealthHistoryPoint = z
    .object({ as_of: IsoDate, score: z.number().int().min(0).max(100), band: HealthBand, config_version: z.number().int().positive() })
    .meta({ id: 'HealthHistoryPoint' });
/** One point per day a snapshot exists, oldest first. Days with no snapshot are absent, not zero. */
export const HealthHistory = z
    .object({ client_id: Uuid, from: IsoDate, to: IsoDate, data: z.array(HealthHistoryPoint) })
    .meta({ id: 'HealthHistory' });
// ---------------------------------------------------------------- GET /at-risk (§8.11 risk-item ranking)
/**
 * Prototype `riskItems()` (Team → At risk). `wait` = a blocked task; `late` = a task 2+ days late, or a client
 * request missing (or within 8 h of) its response time; `quiet` = a client silent 7+ days, or with 3+ overdue
 * engagements and a low or falling product, or 2+ low or falling products.
 */
export const RiskKind = z.enum(['wait', 'late', 'quiet']).meta({ id: 'RiskKind' });
export const RISK_KIND_LABEL = {
    wait: 'Waiting on someone else',
    late: 'Running late',
    quiet: 'Going quiet or falling',
};
export const RiskSubjectKind = z.enum(['task', 'client_request', 'client']).meta({ id: 'RiskSubjectKind' });
export const RiskItem = z
    .object({
    /** Stable key, `<subject kind>:<id>` — the same thing never appears twice. */
    key: z.string(),
    kind: RiskKind,
    subject: z.object({ kind: RiskSubjectKind, id: Uuid, label: z.string(), route: z.string() }),
    client: ClientRef.nullable(),
    /** Who has it now: the assignee, the request owner, or the SPOC for a client. `null` prints "No owner". */
    owner: UserRef.nullable(),
    /**
     * Ranking score, worst first: priority weight (high 3, med 2, low 1) × 2 plus — wait: days blocked + days
     * late; late: days late, or 5 when the response time is missed / 3 when it is near; quiet: min(8, silent
     * days / 2) + overdue engagements / 2.
     */
    score: z.number(),
    /** Why, in one line: "Waiting on Finance for 4 days, and already 2 days late", "Response time missed by 3h". */
    reason: z.string(),
    /** The suggested next step, in one line. */
    next: z.string(),
    /** For a late task: the person with the fewest open tasks who could take it; otherwise `null`. */
    suggested_assignee: UserRef.nullable(),
})
    .meta({ id: 'RiskItem' });
export const AtRiskQuery = PageQuery.extend({ kind: RiskKind.optional(), client_id: Uuid.optional() });
export const RiskItemPage = page(RiskItem)
    .extend({
    /** Over the whole ranking, not this page: the tab's stats and its badge (high-priority clients involved). */
    summary: z.object({
        total: z.number().int().nonnegative(),
        wait: z.number().int().nonnegative(),
        late: z.number().int().nonnegative(),
        quiet: z.number().int().nonnegative(),
        high_priority_clients: z.number().int().nonnegative(),
    }),
})
    .meta({ id: 'RiskItemPage' });
//# sourceMappingURL=health.js.map